import { IBaseGrid } from "Grids/Interfaces";
import { EditContext, Util, Navigation, AppContext } from "Core";
import { GridSettings } from "Configuration";
import { Alignment, CssClass, NotificationTypes } from "Enums";
import { EventFunction, EventArgs, EventHandler, EventObject } from "Core/Events";
import { DropDownService, CommandService, TextBlockService, GridDefinitionService, DatabaseImageService } from "Service";
import { IDataRow } from "Entities";
import { IControl } from "Controls/Interfaces";
import { CONTROL_ID_ATTRIBUTE_NAME } from "Controls/Control";
import { GridEventTypes } from "Core/Events/EventTypes";
import { TooltipExtension } from "./Extension";
import {
    BaseGridDefinition,
    BaseColumnDefinition,
    ControlDefinition,
    UserGridConfigDefinition,
    Environment,
    GridTypes,
    GridControlTypes,
} from "Service/Client";
import CancellationToken from "cancellationtoken";

/**
 * Base class for all grid implementations.
 * @hidden
 */
export abstract class BaseGrid implements IBaseGrid {
    /**
     * @hidden
     */
    protected cancellationTokenSource: {
        token: CancellationToken;
        cancel: (reason?: any) => void;
    };

    /**
     * @hidden
     */
    protected eventHandler: EventHandler<IBaseGrid, GridEventTypes>;

    /**
     * @hidden
     */
    protected editContext: EditContext;

    /**
     * @hidden
     */
    protected jQueryElement: JQuery;

    /**
     * @hidden
     */
    protected gridSettings: GridSettings;

    /**
     * @hidden
     */
    protected baseGridDefinition: BaseGridDefinition;

    /**
     * @hidden
     */
    protected isGridVisible: boolean = true;

    /**
     * @hidden
     */
    public textBlockCategory: string;

    protected environment: Environment;

    /**
     * @hidden
     */
    public isAutoResize = false;

    /**
     * @hidden
     */
    public isUserGridConfigEnabled = false;

    /**
     * @hidden
     */
    protected textBlockService: TextBlockService;

    /**
     * @hidden
     */
    protected commandService: CommandService;

    /**
     * @hidden
     */
    protected comboboxService: DropDownService;

    /**
     * @hidden
     */
    protected databaseImageService: DatabaseImageService;

    /**
     * @hidden
     */
    protected gridDefinitionService: GridDefinitionService;

    /**
     * @hidden
     */
    protected fixedValues: { [columnName: string]: any } = {};

    /**
     * @hidden
     * Height which was set by either defined GridHeight or page resize.
     */
    protected height: string | number;

    /**
     * @hidden
     * The currently selected user grid configuration or null if the default configuration is set.
     */
    protected currentUserGridConfig: UserGridConfigDefinition;

    /**
     * @hidden
     */
    protected tooltipExtension: TooltipExtension;

    private isToastEnabled: boolean;

    private gridScriptContainer: JQuery;

    /**
     * Create a BaseGrid.
     * @param baseGridDefinition BaseGridDefinition.
     * @param gridSettings GridSettings.
     */
    constructor(
        baseGridDefinition: BaseGridDefinition,
        textBlockCategory: string,
        gridSettings: GridSettings,
        environment: Environment,
        comboboxService: DropDownService,
        commandService: CommandService,
        gridDefinitionService: GridDefinitionService,
        textBlockService: TextBlockService,
        databaseImageService: DatabaseImageService
    ) {
        this.gridSettings = gridSettings;
        this.textBlockCategory = textBlockCategory;
        this.baseGridDefinition = baseGridDefinition;
        this.commandService = commandService;
        this.textBlockService = textBlockService;
        this.comboboxService = comboboxService;
        this.gridDefinitionService = gridDefinitionService;
        this.databaseImageService = databaseImageService;
        this.environment = environment;

        this.editContext = new EditContext(
            this.baseGridDefinition,
            comboboxService,
            commandService,
            textBlockService,
            databaseImageService
        );
        this.eventHandler = new EventHandler<IBaseGrid, GridEventTypes>(this);
        this.isToastEnabled = this.environment.IsToastEnabled;
        this.isUserGridConfigEnabled = this.baseGridDefinition.CanUserGridConfig || false;
    }

    /**
     * Get the element which was used for initialization.
     * @since 4.0.0
     */
    public getElement(): HTMLElement {
        return this.jQueryElement[0];
    }

    /**
     * Return the DMS Grid ID.
     * @since 4.0.0
     */
    public getGridId(): number {
        return this.baseGridDefinition.GridID;
    }

    /**
     * Return the DMS Grid name.
     * @since 4.0.0
     */
    public getGridName(): string {
        return this.baseGridDefinition.GridName;
    }

    /**
     * Get GridSettings for this grid.
     * @since 4.0.0
     */
    public getGridSettings(): GridSettings {
        return this.gridSettings;
    }

    /**
     * Trigger the eventHandler for given EventType.
     * @param eventType EventType to be triggered.
     * @param args Additional arguments (ie. EventArgs).
     * @hidden
     */
    public trigger(eventType: GridEventTypes, args?: any | EventArgs<IBaseGrid>) {
        this.eventHandler.trigger(this, eventType, args);
    }

    /**
     * Execute the script / function registered on EventType and returns the result.
     * @param eventType EventType to be triggered.
     * @param args Additional arguments (ie. EventArgs).
     * @hidden
     */
    public execute<TResult, TArgument>(eventType: GridEventTypes, args: TArgument): TResult {
        return this.eventHandler.execute<TResult, TArgument>(this, eventType, args);
    }

    /**
     * Register a function or script for given {@link GridEventTypes}.
     * @param eventType EventType to be registered.
     * @param func Function or script to be registered.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // register function
     * @@grd:GridName@@.on(DMS.Events.GridEventTypes.DataBound, function() { alert('data loaded') });
     *
     * // register function with EventArgs.
     * @@grd:GridName@@.on(DMS.Events.GridEventTypes.DataBound, function(e) { alert(e.sender.hide()); });
     *
     * // register script
     * @@grd:GridName@@.on(DMS.Events.GridEventTypes.DataBound, "alert('data loaded')");
     * ```
     */
    public on(eventType: GridEventTypes, func: string | EventFunction<IBaseGrid> | EventObject<IBaseGrid>): void {
        return this.eventHandler.on(eventType, func);
    }

    /**
     * Register a function or script **for single execution** for given {@link GridEventTypes}.
     * The handler is removed after it was triggered once.
     * @param eventType EventType to be registered.
     * @param func Function or script to be registered.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // register function
     * @@grd:GridName@@.one(DMS.Events.GridEventTypes.DataBound, function() { alert('data loaded') });
     *
     * // register function with EventArgs.
     * @@grd:GridName@@.one(DMS.Events.GridEventTypes.DataBound, function(e) { alert(e.sender.hide()); });
     *
     * // register script
     * @@grd:GridName@@.one(DMS.Events.GridEventTypes.DataBound, "alert('data loaded')");
     * ```
     */
    public one(eventType: GridEventTypes, func: string | EventFunction<IBaseGrid> | EventObject<IBaseGrid>): void {
        return this.eventHandler.one(eventType, func);
    }

    /**
     * Unregisters function or script from {@link GridEventTypes}.
     * @param eventType EventType to be unregistered (omit to unregister all events on this grid).
     * @param func Function or script to be unregistered (omit to unregister all scripts or functions for eventType).
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // unregister click event
     * @@grd:GridName@@.off(DMS.Events.GridEventTypes.DataBound);
     * ```
     */
    public off(eventType?: GridEventTypes, func?: string | EventFunction<IBaseGrid>): void {
        return this.eventHandler.off(eventType, func);
    }

    /**
     * Reload the data of the form / grid.
     * @param ignoreUnsavedChanges (Optional) Ignore and suppress the unsaved changes hint. Default: false
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // Reload the grid with the name GridName.
     * @@grd:GridName@@.reload();
     *
     * // Reload the grid with the name GridName while suppressing the unsaved changes hint.
     * @@grd:GridName@@.reload(true);
     * ```
     */
    public async reload(ignoreUnsavedChanges: boolean = false): Promise<void> {
        let execute: boolean = true;
        if (!ignoreUnsavedChanges && this.baseGridDefinition.EnableChangeTracking && this.hasChanges()) {
            execute = await Navigation.confirmAsync(
                this.textBlockService.get("DiscardUnsavedChangesMessage", this.textBlockCategory),
                this.textBlockService.get("DiscardUnsavedChangesTitle", this.textBlockCategory)
            );
        }
        if (execute) {
            await this.reloadInternal();
        }
    }
    /**
     * Reloads data of this grid.
     * @hidden
     */
    protected abstract reloadInternal(): Promise<void>;

    /**
     * Load the data of the grid on initialization.
     */
    public async initialLoad(): Promise<void> {
        if (this.baseGridDefinition.ShowDefault) {
            await this.reload();
        }
        this.baseGridDefinition.ShowDefault = true;
    }

    /**
     * Reinitialize the grid by reloading the Grid Definition. After that, the data is reloaded.
     * This is needed when the grid appearance is dependent on filter values.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@grd:GridName@@.reinitialize();
     * ```
     */
    public reinitialize(): void {
        this.trigger(GridEventTypes.ReinitializeGrid);
    }

    /**
     * Get the type of the Grid.
     * @since 4.0.0
     */
    public getGridType(): GridTypes {
        return this.baseGridDefinition.GridTypeID;
    }

    /**
     * Dispose the EditContext and release all controls.
     * @hidden
     */
    protected disposeEditContext(): void {
        if (this.editContext.isInitialized) {
            this.editContext.dispose();
        }
    }

    /**
     * Returns the current {EditContext}.
     * @hidden
     */
    public getEditContext(): EditContext {
        return this.editContext;
    }

    /**
     * Checks if notfiy is enabled and notifies if so.
     * @hidden
     */
    public notifyIfEnabled(title: string, type: NotificationTypes, text?: string) {
        if (this.isToastEnabled) {
            Navigation.notify(title, type, text);
        }
    }

    /**
     * Disable notifications for this grid.
     * @since 4.2.0.0
     * @example
     * ```JavaScript
     * @@grd:GridName@@.disableNotification();
     * ```
     */
    public disableNotification(): void {
        this.isToastEnabled = false;
    }

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected abstract setElementInternal(element: JQuery): Promise<void>;

    /**
     * Sets the jQuery-Element to initialize the grid.
     * @param element JQuery-Wrapper in which the grid is initialized.
     * @hidden
     */
    public async setElement(element: JQuery): Promise<void> {
        this.jQueryElement = AppContext.current.validateElement(element);

        // The grids may contain controls which are invisible for the user, but have a default value set.
        // To include those default values in the filter as parameters for the grid, the formDefinition contains those values in a separate list.
        // Also in add-mode of a form/grid, the controls default value needs to be inserted when the control is invisible.
        // This list is traversed and the values are converted to actual value which get returned in {getData}.
        if (this.baseGridDefinition.FixedValues && this.baseGridDefinition.FixedValues.length) {
            for (let i = 0, len = this.baseGridDefinition.FixedValues.length; i < len; i++) {
                let fixedFilterValueDefinition = this.baseGridDefinition.FixedValues[i];
                let controlDefinition: ControlDefinition = {
                    ControlID: fixedFilterValueDefinition.ControlID,
                    ControlTypeID: fixedFilterValueDefinition.ControlTypeID,
                };

                let ctl = AppContext.current.initializeControl(controlDefinition, this.textBlockCategory, this.environment);
                let dataType = ctl.getModelDatatype();
                let defaultValue = Util.resolve(fixedFilterValueDefinition.Value, {});
                this.fixedValues[fixedFilterValueDefinition.ColumnName] = Util.parse(dataType, defaultValue, "en-US");
            }
        }

        await this.setElementInternal(element);

        if (!this.baseGridDefinition.ShowDefault) {
            this.hide();
        }

        // add container for scripts
        if (this.baseGridDefinition.GridScriptDefinition && this.baseGridDefinition.GridScriptDefinition.length > 0) {
            this.gridScriptContainer = $(`<div class="dms-gridscript"></div>`).appendTo(this.getOuterElement());
            for (let i = 0, len = this.baseGridDefinition.GridScriptDefinition.length; i < len; i++) {
                try {
                    let script = Util.resolve(this.baseGridDefinition.GridScriptDefinition[i].Script, {});
                    this.gridScriptContainer.append(script);
                } catch (e) {
                    console.log("Error in GridScript: " + e);
                }
            }
        }

        this.preloadTextBlocks();
    }

    /**
     * Hides the grid.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@grd:GridName@@.hide();
     * ```
     */
    public hide(): void {
        if (this.isGridVisible && this.jQueryElement != null) {
            this.isGridVisible = false;
            this.getOuterElement().hide();
            this.trigger(GridEventTypes.ResizeRequested);
        }
    }

    /**
     * Shows the grid if it was invisible / hidden.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@grd:GridName@@.show();
     * ```
     */
    public show(): void {
        if (!this.isGridVisible && this.jQueryElement != null) {
            this.isGridVisible = true;
            this.getOuterElement().show();
            this.trigger(GridEventTypes.ResizeRequested);
        }
    }

    /**
     * Returns true if the control is visible.
     * @since 4.0.1
     * @example
     * ```JavaScript
     * var visible = @@grd:GridName@@.isVisible();
     * ```
     */
    public isVisible(): boolean {
        return this.isGridVisible;
    }

    /**
     * Clears all grid data.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@grd:GridName@@.clear();
     * ```
     */
    public clear(): void {}

    /**
     * Show/hide the loading panel for the grid.
     * @param isBusy True if the loading panel should be shown. False to hide the loading panel.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@grd:GridName@@.setBusy(true); // show loading panel
     * ```
     */
    public setBusy(isBusy: boolean): void {
        kendo.ui.progress(this.jQueryElement, isBusy);
    }

    /**
     * Gets the CssClass for a given ColumnDefinition.
     * @param columnDefinition Given ColumnDefinition.
     * @hidden
     */
    protected getColumnCssClass(columnDefinition: BaseColumnDefinition): string {
        let css = "dms-column ";
        switch (columnDefinition.AlignmentID) {
            case Alignment.Left:
                css += CssClass.alignLeft;
                break;
            case Alignment.Middle:
                css += CssClass.alignCenter;
                break;
            case Alignment.Right:
                css += CssClass.alignRight;
                break;
        }
        if (columnDefinition.CssClass) {
            css += " " + columnDefinition.CssClass;
        }
        if (columnDefinition.Controls && columnDefinition.Controls.length > 1) {
            let controlCnt = 0;
            for (let i = 0, len = columnDefinition.Controls.length; i < len; i++) {
                let controlDefinition = columnDefinition.Controls[i];
                let ignoreWidth =
                    (controlDefinition.ControlTypeID >= GridControlTypes.Edit &&
                        controlDefinition.ControlTypeID <= GridControlTypes.Link) ||
                    controlDefinition.ControlTypeID == GridControlTypes.Image; // 101-106 = Grid Commands, 107 = Link, 7 = Image

                if (!Util.stringIsNullOrEmpty(controlDefinition.Width) || ignoreWidth) {
                    if (++controlCnt == 2) {
                        css += " dms-inline";
                    }
                }
            }
            if (css.indexOf("dms-inline") == -1) {
                css += " dms-block";
            }
        }

        return css;
    }

    /**
     * Get the outer element (grid wrapper) of the grid.
     * @since 4.1.0
     */
    public getOuterElement(): JQuery {
        return this.jQueryElement.closest(".dms-outer-element");
    }

    /**
     * Set the height of the grid.
     * @param height Height in pixel.
     * @since 4.0.0
     */
    public setHeight(height: string | number): void {
        this.height = height;
        this.getOuterElement().height(height);
    }

    /**
     * Initialize tooltips.
     * @hidden
     */
    protected initializeTooltips(): void {
        if (!this.tooltipExtension) {
            this.tooltipExtension = new TooltipExtension();
        }
        this.tooltipExtension.initialize(this.getOuterElement());
    }

    /**
     * Resolve a script or link.
     * @param script Script to be resolved.
     * @param data Data for resolving.
     * @hidden
     */
    protected resolve(script: string, data: IDataRow): string {
        try {
            if (data == null) {
                data = {};
            }
            return Util.resolve(script, data);
        } catch (e) {
            console.log(e);
            return script;
        }
    }

    /**
     * Get the fieldName from the control.
     * @param ctr ControlDefinition.
     * @hidden
     */
    protected getControlFieldName(ctr: ControlDefinition): string {
        return ctr.ValueColumnName;
    }

    /**
     * Finds and initializes all controls in given container.
     * @param container Container which holds the jQuery-Elements of the control templates.
     * @param controls List of registered controls.
     * @param editContext The editContext where the control should be registered. Leave empty for the default editContext.
     * @hidden
     */
    protected initializeControlsInContainer(
        container: JQuery,
        controls: { [controlId: number]: IControl },
        editContext: EditContext = null
    ): void {
        // get all elements with the id attribute from the container
        let containers = container.find(`[${CONTROL_ID_ATTRIBUTE_NAME}]`);
        for (let i = 0, len = containers.length; i < len; i++) {
            try {
                let element = $(containers[i]);
                let controlId = Number(element.attr(CONTROL_ID_ATTRIBUTE_NAME));
                let control = controls[controlId];
                if (control == null) {
                    throw new Error(`Control ${controlId} not available.`);
                }
                this.registerControlEvents(control);

                (editContext || this.editContext).registerControl(control);
                control.setElement(element);
            } catch (ex) {
                console.log(ex);
            }
        }
    }

    /**
     * Store original data row values in the key __original
     * @param rows List of data rows which values should be stored.
     * @param grouping Sets wether grouping is enabled for this result.
     * @hidden
     */
    protected storeOriginalDataRow(rows: any[], grouping = false) {
        if (!rows) {
            return;
        }

        for (const item of rows) {
            if (grouping && item.items && item.items.length) {
                this.storeOriginalDataRow(item.items, grouping);
            } else {
                item.__original = Object.assign({}, item);
            }
        }
    }

    /**
     * Register additional events for this control.
     * @param control Control where controls can be registered.
     * @hidden
     */
    protected registerControlEvents(control: IControl) {}

    /**
     * Find control in EditContext. Used in $dms-Selector
     * @param controlId ControlId to find.
     * @hidden
     */
    public findControl(control: number | HTMLElement): IControl {
        return this.editContext.findControl(control);
    }

    /**
     * Apply the given user grid config to the grid.
     * @param config Selected configuration.
     * @hidden
     */
    public applyUserGridConfig(config: UserGridConfigDefinition): Promise<void> {
        this.currentUserGridConfig = config;
        return Promise.resolve();
    }

    /**
     * Set the given user grid config initially to the grid.
     * @param config Selected configuration.
     * @hidden
     */
    public setInitialUserGridConfig(config: UserGridConfigDefinition): void {
        this.currentUserGridConfig = config;
    }

    /**
     * Get the current state of the grid as user grid config.
     * @hidden
     */
    public createUserGridConfig(): string {
        return null;
    }

    public canNavigate(): boolean {
        return true;
    }

    /**
     * Gets the current edited (or added) data row.
     * @since 4.5.0
     */
    public abstract getCurrentDataRow(): IDataRow;

    /**
     * Gets wether the grid has unsaved changes.
     * @since 4.5.0
     */
    public hasChanges(): boolean {
        const row = this.getCurrentDataRow();
        return (row && row.dirty) || false;
    }

    /**
     * Gets wether the grid should report unsaved changes before unload.
     * @hidden
     */
    public isChangeTrackingEnabled(): boolean {
        return this.baseGridDefinition.EnableChangeTracking;
    }

    /**
     * Dispose the grid and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        this.off();

        if (this.tooltipExtension) {
            this.tooltipExtension.dispose();
        }

        if (this.jQueryElement) {
            this.jQueryElement.off();
        }

        if (this.gridScriptContainer) {
            this.gridScriptContainer.empty();
            this.gridScriptContainer.remove();
        }
    }

    private preloadTextBlocks() {
        if (this.baseGridDefinition.EnableChangeTracking) {
            this.textBlockService.get("DiscardUnsavedChangesMessage", this.textBlockCategory);
            this.textBlockService.get("DiscardUnsavedChangesTitle", this.textBlockCategory);
        }
    }
}
